import * as React from "react";
import { Drawer, Button, Empty } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  selectIsNotificationDrawerOpen,
  selectNotifications,
  closeDrawer,
  removeNotification,
  clearAll,
  setNotificationOrg,
  syncFromStorage,
  getStorageKey,
  NotificationItem as NotificationItemType,
} from "../../store/notificationDrawerSlice";
import { selectOrg } from "../../store/contextSlice";
import { NotificationItem } from "./NotificationItem";
import "./index.css";

export const NotificationDrawer: React.FC = () => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector(selectIsNotificationDrawerOpen);
  const notifications = useAppSelector(selectNotifications);
  const org = useAppSelector(selectOrg);

  // Sync notifications with current org
  React.useEffect(() => {
    if (org) {
      dispatch(setNotificationOrg(org));
    }
  }, [org, dispatch]);

  // Listen for storage events from other tabs
  React.useEffect(() => {
    if (!org) return;

    const storageKey = getStorageKey(org);

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === storageKey) {
        dispatch(syncFromStorage());
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [org, dispatch]);

  // Force z-index on drawer container after render
  React.useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        const root = document.querySelector(".notification-drawer-root") as HTMLElement;
        if (root) {
          // Set z-index on the parent container div
          const parentDiv = root.parentElement as HTMLElement;
          if (parentDiv) {
            parentDiv.style.position = "relative";
            parentDiv.style.zIndex = "100000";
          }
        }
      }, 0);
    }
  }, [isOpen]);

  const handleClose = () => {
    dispatch(closeDrawer());
  };

  const handleRemove = (id: string) => {
    dispatch(removeNotification(id));
  };

  const handleClearAll = () => {
    dispatch(clearAll());
  };

  return (
    <Drawer
      rootClassName="notification-drawer-root"
      title={
        <div className="flex items-center justify-between">
          <span>Notifications</span>
          {notifications.length > 0 && (
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              onClick={handleClearAll}
              className="opacity-60 hover:opacity-100"
            >
              Clear all
            </Button>
          )}
        </div>
      }
      placement="right"
      onClose={handleClose}
      open={isOpen}
      width={400}
      zIndex={1100}
      className="notification-drawer"
      mask={true}
      maskClosable={true}
    >
      <div className="notification-drawer-content">
        {notifications.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No notifications"
            className="mt-8"
          />
        ) : (
          notifications.map((item: NotificationItemType) => (
            <NotificationItem key={item.id} item={item} onRemove={handleRemove} />
          ))
        )}
      </div>
    </Drawer>
  );
};

export { NotificationBellButton } from "./NotificationBellButton";
